import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GuideTour } from "../../GuideTour/GuideTour";
import { useGuideTour } from "../../GuideTour/useGuideTour";
import {
  WhitelabelPaymentHelpShell,
  type WhitelabelPaymentHelpShellRef,
} from "./WhitelabelPaymentHelpShell";
import {
  WL_PAYMENT_HELP_STEPS_BY_METHOD,
  type WhitelabelPaymentHelpMethod,
} from "./paymentHelpSteps";
import { WL_PAYMENT_HELP_TOUR_STORAGE_KEY } from "../whitelabelTourStorage";

function waitMs(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = window.setTimeout(() => {
      if (signal.aborted) reject(new DOMException("Aborted", "AbortError"));
      else resolve();
    }, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

const HELP = {
  spotlight: 1100,
  branch: 900,
  transition: 600,
} as const;

export function parseWhitelabelPaymentHelpMethod(
  raw: string | null
): WhitelabelPaymentHelpMethod | null {
  if (raw === "crypto" || raw === "fiat" || raw === "offline") return raw;
  return null;
}

type TourApi = NonNullable<ReturnType<typeof useGuideTour>>;

async function runPaymentHelpSegment(
  stepIndex: number,
  method: WhitelabelPaymentHelpMethod,
  signal: AbortSignal,
  sh: WhitelabelPaymentHelpShellRef,
  T: TourApi,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  totalSteps: number
): Promise<void> {
  const steps = WL_PAYMENT_HELP_STEPS_BY_METHOD[method];
  const step = steps[stepIndex];
  if (!step) return;

  T.jumpToStep(stepIndex);
  await waitMs(HELP.transition, signal);

  if (method === "crypto") {
    if (step.id === "pay-crypto-currency") {
      sh.clearCryptoCurrencyForTour();
      await waitMs(260, signal);
      await sh.pulseControl("#wl-pay-crypto-dropdown-trigger");
      await waitMs(360, signal);
      sh.openCryptoDropdownForTour();
      await waitMs(480, signal);
      await sh.pulseControl("#wl-pay-crypto-dropdown-list");
      await waitMs(340, signal);
      await sh.pulseControl("#wl-pay-crypto-option-usdt-eth");
      await waitMs(280, signal);
      sh.selectUsdtEthereumForTour();
      await waitMs(420, signal);
    }
    if (step.id === "pay-crypto-deposit") {
      sh.selectCryptoCurrencyForTour();
      await waitMs(420, signal);
    }
  }

  if (method === "fiat") {
    if (step.id === "pay-fiat-upi") sh.setFiatTab("upi");
    if (step.id === "pay-fiat-bank") sh.setFiatTab("bank");
    if (step.id === "pay-fiat-upi" || step.id === "pay-fiat-bank") {
      await waitMs(320, signal);
    }
    if (step.id === "pay-fiat-proof") {
      sh.simulateFiatProofUploaded();
      await waitMs(520, signal);
    }
  }

  await waitMs(HELP.spotlight, signal);
  await sh.pulseControl(step.selector);
  await waitMs(HELP.branch, signal);

  if (method === "fiat" && step.id === "pay-fiat-submit") {
    await waitMs(320, signal);
    sh.clickFiatSubmitDemo();
    await waitMs(550, signal);
  }

  if (stepIndex === totalSteps - 1) {
    T.complete();
    navigate("/whitelabel", { replace: true });
  }
}

function WhitelabelPaymentHelpTourInner({ method }: { method: WhitelabelPaymentHelpMethod }) {
  const navigate = useNavigate();
  const steps = WL_PAYMENT_HELP_STEPS_BY_METHOD[method];

  const [helpOrchestratorGeneration, setHelpOrchestratorGeneration] = useState(0);
  const shellRef = useRef<WhitelabelPaymentHelpShellRef | null>(null);
  const tourRef = useRef<ReturnType<typeof useGuideTour> | null>(null);
  const orchestratorAbortRef = useRef<AbortController | null>(null);
  const resumeFromStepRef = useRef<number | null>(null);

  const handleHelpExit = useCallback(() => {
    tourRef.current?.skip();
    navigate("/whitelabel", { replace: true });
  }, [navigate]);

  const tour = useGuideTour({
    steps,
    storageKey: WL_PAYMENT_HELP_TOUR_STORAGE_KEY,
    flowStepIndex: 0,
    persistCompletion: false,
    onEscape: handleHelpExit,
  });
  tourRef.current = tour;

  useEffect(() => {
    tour.start();
    return () => {
      tour.stop();
    };
  }, [tour.start, tour.stop]);

  useEffect(() => {
    if (!tour.isActive) return;

    const ac = new AbortController();
    orchestratorAbortRef.current = ac;
    const signal = ac.signal;

    const run = async () => {
      const waitForShell = async () => {
        for (let i = 0; i < 40; i += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          if (shellRef.current) return shellRef.current;
          await waitMs(50, signal);
        }
        return shellRef.current;
      };

      try {
        await waitMs(0, signal);
        const sh = await waitForShell();
        if (!sh) return;

        const from = resumeFromStepRef.current ?? 0;
        resumeFromStepRef.current = null;

        const T = tourRef.current;
        if (!T) return;

        for (let s = from; s < steps.length; s += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          await runPaymentHelpSegment(s, method, signal, sh, T, navigate, steps.length);
          if (!tourRef.current?.isActive) return;
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error(e);
      }
    };

    void run();
    return () => {
      ac.abort();
      orchestratorAbortRef.current = null;
    };
  }, [helpOrchestratorGeneration, method, navigate, steps.length, tour.isActive]);

  const handleHelpTourPrev = useCallback(() => {
    const t = tourRef.current;
    if (!t || t.stepIndex <= 0) return;
    resumeFromStepRef.current = Math.max(0, t.stepIndex - 1);
    orchestratorAbortRef.current?.abort();
    t.goPrev();
    setHelpOrchestratorGeneration((g) => g + 1);
  }, []);

  const helpAutopilotRunning = tour.isActive;

  return (
    <>
      <WhitelabelPaymentHelpShell
        ref={shellRef}
        method={method}
        blockPointerForAutopilot={helpAutopilotRunning}
      />
      <GuideTour
        isActive={tour.isActive}
        targetRect={tour.targetRect}
        stepIndex={tour.stepIndex}
        totalSteps={tour.totalSteps}
        content={tour.activeStep?.content ?? ""}
        demoKind={tour.activeStep?.demoKind}
        spotlightPadding={tour.activeStep?.spotlightPadding ?? 8}
        autopilotMode={helpAutopilotRunning}
        autopilotShowPrevious={helpAutopilotRunning}
        onNext={tour.goNext}
        onPrev={handleHelpTourPrev}
        onSkip={handleHelpExit}
      />
    </>
  );
}

export default function WhitelabelPaymentHelpTourPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const method = parseWhitelabelPaymentHelpMethod(searchParams.get("method"));

  useEffect(() => {
    if (!method) {
      navigate("/whitelabel", { replace: true });
    }
  }, [method, navigate]);

  if (!method) {
    return null;
  }

  return <WhitelabelPaymentHelpTourInner method={method} />;
}
